import type { Country } from '@backmarket/http-api'

import type {
  Address,
  BusinessDetails,
  Stakeholder,
  StakeholderRole,
} from '~/api/kyb/types'
import type {
  BranchAddress,
  BusinessFormValues,
} from '~/scopes/kyb/pages/components/steps/BusinessDetails/BusinessDetails.types'
import type { StakeholderFormValues } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.types'

export function transformStakeholderFormToApi(
  formValues: StakeholderFormValues,
  roles: StakeholderRole[],
  id?: string | null,
): Stakeholder {
  const { photoIdFront } = formValues
  if (!photoIdFront) {
    throw new Error('transformStakeholderFormToApi: photoIdFront is required')
  }

  return {
    id: id ?? null,
    roles,
    firstName: formValues.firstName,
    kanaFirstName: formValues.kanaFirstName,
    latinFirstName: formValues.latinFirstName,
    lastName: formValues.lastName,
    kanaLastName: formValues.kanaLastName,
    latinLastName: formValues.latinLastName,
    dateOfBirth: formValues.dateOfBirth,
    countryOfBirth: formValues.countryOfBirth,
    countryOfCitizenship: formValues.countryOfCitizenship,
    phoneNumber: formValues.phoneNumber,
    countryOfResidency: formValues.countryOfResidency,
    address: {
      addressLine1: formValues.addressLine1,
      addressLine2: formValues.addressLine2 || null,
      postalCode: formValues.postalCode,
      city: formValues.city,
      stateOrProvince: null,
      country: formValues.country as string,
    },
    identityDocument: {
      identificationType: formValues.identificationType,
      countryOfIssue: formValues.countryOfIssue,
      front: photoIdFront,
      back: formValues.photoIdBack ?? null,
    },
  }
}

export function createStakeholderFormValues(
  stakeholder: Stakeholder | undefined,
): StakeholderFormValues {
  return {
    id: stakeholder?.id ?? null,
    firstName: stakeholder?.firstName ?? '',
    kanaFirstName: stakeholder?.kanaFirstName ?? '',
    latinFirstName: stakeholder?.latinFirstName ?? '',
    lastName: stakeholder?.lastName ?? '',
    kanaLastName: stakeholder?.kanaLastName ?? '',
    latinLastName: stakeholder?.latinLastName ?? '',
    dateOfBirth: stakeholder?.dateOfBirth ?? '',
    countryOfBirth: stakeholder?.countryOfBirth ?? '',
    countryOfCitizenship: stakeholder?.countryOfCitizenship ?? '',
    phoneNumber: stakeholder?.phoneNumber ?? '',
    countryOfResidency: stakeholder?.countryOfResidency ?? '',
    addressLine1: stakeholder?.address?.addressLine1 ?? '',
    addressLine2: stakeholder?.address?.addressLine2 ?? '',
    postalCode: stakeholder?.address?.postalCode ?? '',
    city: stakeholder?.address?.city ?? '',
    country: (stakeholder?.address?.country as Country | '') ?? '',
    identificationType: stakeholder?.identityDocument?.identificationType ?? '',
    countryOfIssue: stakeholder?.identityDocument?.countryOfIssue ?? '',
    photoIdFront: undefined,
    photoIdBack: undefined,
  }
}

export function createEmptyStakeholderFormValues(): StakeholderFormValues {
  return createStakeholderFormValues(undefined)
}

export function createBusinessDetailsFormValuesFromApi(
  businessDetails?: BusinessDetails,
  branchesAddresses?: BranchAddress[],
): BusinessFormValues {
  return {
    legalName: businessDetails?.legalName ?? '',
    tradeName: businessDetails?.tradeName ?? '',
    kanaLegalName: businessDetails?.legalName ?? '',
    latinLegalName: businessDetails?.legalName ?? '',
    email: businessDetails?.email ?? '',
    registrationCountry: businessDetails?.registrationCountry ?? '',
    registrationType: businessDetails?.registrationType ?? '',
    registrationNumber: businessDetails?.registrationNumber ?? '',
    secondhandDealerNumber: businessDetails?.secondhandDealerNumber ?? '',
    secondhandDealerNumberIssuingPrefecture:
      businessDetails?.secondhandDealerNumberIssuingPrefecture ?? '',
    proofOfSecondHandDealerLicence:
      businessDetails?.proofOfSecondHandDealerLicence,
    incorporationDate: businessDetails?.incorporationDate ?? '',
    headquartersStreet:
      businessDetails?.headquartersAddress?.addressLine1 ?? '',
    headquartersStreet2:
      businessDetails?.headquartersAddress?.addressLine2 ?? '',
    headquartersCity: businessDetails?.headquartersAddress?.city ?? '',
    headquartersPostalCode:
      businessDetails?.headquartersAddress?.postalCode ?? '',
    headquartersCountry:
      (businessDetails?.headquartersAddress?.country as Country | '') ?? '',
    headquartersStateOrProvince:
      businessDetails?.headquartersAddress?.stateOrProvince ?? '',
    registeredBusinessStreet:
      businessDetails?.registeredBusinessAddress?.addressLine1 ?? '',
    registeredBusinessStreet2:
      businessDetails?.registeredBusinessAddress?.addressLine2 ?? '',
    registeredBusinessCity:
      businessDetails?.registeredBusinessAddress?.city ?? '',
    registeredBusinessPostalCode:
      businessDetails?.registeredBusinessAddress?.postalCode ?? '',
    registeredBusinessCountry:
      (businessDetails?.registeredBusinessAddress?.country as Country | '') ??
      '',
    registeredBusinessStateOrProvince:
      businessDetails?.registeredBusinessAddress?.stateOrProvince ?? '',
    branchesAddresses: branchesAddresses || [],
    w9: businessDetails?.w9,
    articleOfIncorporation: businessDetails?.articleOfIncorporation,
    investedCapitalCurrency: businessDetails?.investedCapital?.currency ?? '',
    investedCapitalAmount: parseFloat(
      businessDetails?.investedCapital?.amount ?? '0',
    ),
  }
}

export function createBranchAddressFromApi(address: Address): BranchAddress {
  return {
    street: address.addressLine1,
    street2: address.addressLine2 ?? '',
    city: address.city,
    postalCode: address.postalCode,
    country: address.country as Country | '',
  }
}

export function transformBusinessDetailsFormToApi(
  formValues: BusinessFormValues,
): BusinessDetails {
  const { articleOfIncorporation } = formValues

  if (formValues.investedCapitalCurrency === '') {
    // This would be caught earlier by form validation,
    // but typescript doens’t know it.
    throw new Error('Invested capital currency cannot be empty')
  }

  return {
    legalName: formValues.legalName,
    tradeName: formValues.tradeName,
    kanaLegalName: formValues.kanaLegalName || undefined,
    latinLegalName: formValues.latinLegalName || undefined,
    registrationCountry: formValues.registrationCountry,
    registrationType: formValues.registrationType,
    registrationNumber: formValues.registrationNumber,
    incorporationDate: formValues.incorporationDate,
    headquartersAddress: {
      addressLine1: formValues.headquartersStreet,
      addressLine2: formValues.headquartersStreet2 || null,
      city: formValues.headquartersCity,
      postalCode: formValues.headquartersPostalCode,
      country: formValues.headquartersCountry as string,
      stateOrProvince: formValues.headquartersStateOrProvince || null,
    },
    branchesAddresses: formValues.branchesAddresses.map((branch) => ({
      addressLine1: branch.street,
      addressLine2: branch.street2 || null,
      city: branch.city,
      postalCode: branch.postalCode,
      stateOrProvince: null,
      country: branch.country as string,
    })),
    registeredBusinessAddress: {
      addressLine1: formValues.registeredBusinessStreet,
      addressLine2: formValues.registeredBusinessStreet2 || null,
      city: formValues.registeredBusinessCity,
      postalCode: formValues.registeredBusinessPostalCode,
      stateOrProvince: formValues.registeredBusinessStateOrProvince,
      country: formValues.registeredBusinessCountry as string,
    },
    email: formValues.email,
    investedCapital: {
      currency: formValues.investedCapitalCurrency,
      amount: String(formValues.investedCapitalAmount || ''),
    },
    articleOfIncorporation,
    secondhandDealerNumber: formValues.secondhandDealerNumber || undefined,
    secondhandDealerNumberIssuingPrefecture:
      formValues.secondhandDealerNumberIssuingPrefecture || undefined,
    proofOfSecondHandDealerLicence: formValues.proofOfSecondHandDealerLicence,
    w9: formValues.w9,
  }
}
